/**
 * Admin Seat-Billing Reconciliation RPC definitions.
 *
 * A single admin-gated, read-only endpoint — `admin/billing/seatDriftReport` —
 * that runs the seat-billing reconciliation on demand and returns the full
 * report: per direct customer, any drift between their Stripe subscription
 * quantity/Price and their true active-agent count. The same computation the
 * daily {@link SeatBillingReconciler} sweep runs, exposed for the admin Billing
 * page (phase 2 renders it) and CLI consumption via the admin service token.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, SubscriptionError, Unauthorized } from "../errors.ts";

// ============================================================================
// Response schemas
// ============================================================================

/**
 * The kind of seat-billing drift — mirrors {@link SeatDriftKind}.
 *
 * @since 1.0.0
 */
export const SeatDriftKind = Schema.Literal("quantity_mismatch", "price_mismatch", "legacy_shape");
export type SeatDriftKind = Schema.Schema.Type<typeof SeatDriftKind>;

/**
 * A single detected drift for one customer.
 *
 * @since 1.0.0
 */
export const SeatDrift = Schema.Struct({
  customerId: Schema.String,
  kind: SeatDriftKind,
  expected: Schema.String,
  actual: Schema.String,
});
export type SeatDrift = Schema.Schema.Type<typeof SeatDrift>;

/**
 * The full reconciliation report — the scanned direct-customer subscription
 * count and the drifts found.
 *
 * @since 1.0.0
 */
export const SeatDriftReport = Schema.Struct({
  scanned: Schema.Number,
  drifts: Schema.Array(SeatDrift),
});
export type SeatDriftReport = Schema.Schema.Type<typeof SeatDriftReport>;

// ============================================================================
// Request schemas
// ============================================================================

/**
 * `seatDriftReport` takes no arguments — the sweep is global.
 *
 * @since 1.0.0
 */
export const SeatDriftReportInput = Schema.Struct({});
export type SeatDriftReportInput = Schema.Schema.Type<typeof SeatDriftReportInput>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Seat-Billing RPC group. The single endpoint requires admin role via
 * `AdminMiddleware`; the tag is namespaced under `admin/billing/`.
 *
 * @since 1.0.0
 */
export class AdminSeatBillingRpcs extends RpcGroup.make(
  Rpc.make("seatDriftReport", {
    payload: SeatDriftReportInput,
    success: SeatDriftReport,
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError, SubscriptionError),
  }).middleware(AdminMiddleware),
).prefix("admin/billing/") {}
